import { Form, Formik, FormikHelpers } from 'formik';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { selectPermissionScopeDetail } from '@bom-nextgen-keycloak/web/core';
import {
  ScopeForm,
  validationSchema,
} from '@bom-nextgen-keycloak/web/pages/feature-scope';
import { FeaturePage } from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  FormActionsGroup,
  FormSpinner,
  ProtectedRouter,
} from '@bom-nextgen-keycloak/web/shared/ui';
import { getButtonStatus } from '@bom-nextgen-keycloak/web/shared/util';
import { useScopeDetail2 } from '../../hooks';
import { PARENT_PATH } from '../../shared';

type Form = {
  displayName: string;
  name: string;
};

const General: FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, handleUpdate, isLoading, isUpdating } = useScopeDetail2(id);
  const { isEditGeneralTab: canEdit } = useSelector(
    selectPermissionScopeDetail
  );

  const initialValues: Form = {
    displayName: data?.displayName ?? '',
    name: data?.name ?? '',
  };

  const handleCancel = () => {
    navigate(PARENT_PATH);
  };

  return isLoading ? (
    <FormSpinner />
  ) : (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(payload: Form, { setSubmitting }: FormikHelpers<Form>) => {
        handleUpdate({
          ...data,
          displayName: payload.displayName,
          id: data?.id || '',
          name: payload.name,
        });
        setSubmitting(false);
      }}
      validationSchema={validationSchema}
    >
      {({ dirty, isValid, resetForm }) => {
        const { disabled: buttonDisabled } = getButtonStatus({
          canEdit,
          isDirty: dirty,
          isLoading: isUpdating,
          isValid,
        });

        return (
          <Form>
            <ScopeForm isEdit />
            <FormActionsGroup
              disableCancel={buttonDisabled.cancel}
              disableReset={buttonDisabled.reset}
              disableSave={buttonDisabled.save}
              loading={isUpdating}
              onCancel={handleCancel}
              onReset={resetForm}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

const WrappedScopeGeneralPage: FC = () => {
  const { canViewGeneralTab } = useSelector(selectPermissionScopeDetail);

  return (
    <ProtectedRouter
      canView={canViewGeneralTab}
      feature={FeaturePage.SCOPE}
      isEditPage
      navigatePath={PARENT_PATH}
    >
      <General />
    </ProtectedRouter>
  );
};

export { WrappedScopeGeneralPage };
